import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { FaArrowLeft, FaFileInvoiceDollar, FaFileImport, FaFileExcel, FaFileDownload, FaPrint } from 'react-icons/fa';
import api from '../../services/api';

// Colonnes attendues d'un export "Paie du Mois" (voir PaieMoisPage.js)
const EXPECTED_COLUMNS = ['ID_Ouvrier', 'Ouvrier', 'CIN', 'Taux', 'Qualification', 'Dernier_Chantier', 'Jours', 'Heures', 'salaires'];
const NUMERIC_COLUMNS = ['Taux', 'Jours', 'Heures', 'salaires'];

// Sociétés proposées en cards — pour l'instant seulement celles-ci sont utilisées
const FEATURED_SOCIETES = ['PISTAS', 'RUTAS'];

// ── Détection de colonnes mal nommées (typo, casse, séparateur différent...) ──
const normalizeColName = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
};

// Cherche, parmi les colonnes du fichier importé (non déjà utilisées), celle qui ressemble le plus
// à la colonne attendue — exact (accents/casse/séparateur ignorés) > sous-chaîne > distance d'édition.
const findClosestColumn = (expected, actualCols, usedCols) => {
  const normExpected = normalizeColName(expected);
  const candidates = actualCols.filter((c) => !usedCols.has(c));

  let match = candidates.find((c) => normalizeColName(c) === normExpected);
  if (match) return { column: match, confidence: 'exact' };

  match = candidates.find((c) => {
    const nc = normalizeColName(c);
    return nc.length > 2 && (nc.includes(normExpected) || normExpected.includes(nc));
  });
  if (match) return { column: match, confidence: 'partial' };

  let best = null;
  let bestDist = Infinity;
  candidates.forEach((c) => {
    const d = levenshtein(normalizeColName(c), normExpected);
    if (d < bestDist) { bestDist = d; best = c; }
  });
  if (best && bestDist <= Math.max(2, Math.floor(normExpected.length * 0.4))) {
    return { column: best, confidence: 'fuzzy' };
  }

  return null;
};

const MiniCard = ({ label, active, onClick, logo }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
      border: `1.5px solid ${active ? '#0d6efd' : '#dee2e6'}`,
      background: active ? '#0d6efd' : '#fff',
      color: active ? '#fff' : '#333',
      fontSize: '0.8rem', fontWeight: 600,
      boxShadow: active ? '0 0 0 2px rgba(13,110,253,0.25)' : 'none',
      transition: 'all 0.15s',
    }}
  >
    {logo && <img src={logo} alt="" style={{ height: 16, objectFit: 'contain' }} />}
    {label}
  </button>
);

// Récupère les dimensions naturelles d'un logo (data URI) pour l'insérer dans le PDF sans le déformer.
const loadImageDims = (dataUri) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
  img.onerror = () => resolve(null);
  img.src = dataUri;
});

const fitLogo = (dims, maxW, maxH) => {
  if (!dims || !dims.w || !dims.h) return { w: maxW, h: maxH };
  const ratio = Math.min(maxW / dims.w, maxH / dims.h);
  return { w: dims.w * ratio, h: dims.h * ratio };
};

// Les montants viennent souvent formatés en texte FR ("2 930,00") plutôt qu'en nombre natif.
const parseFrNumber = (val) => {
  if (typeof val === 'number') return val;
  if (val == null) return 0;
  const cleaned = String(val).replace(/[\s ]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

// toLocaleString('fr-FR') sépare les milliers avec une espace "spéciale" (U+00A0 ou U+202F selon
// le navigateur/Node) que la police standard "helvetica" de jsPDF affiche en "/". On neutralise le
// souci en ne gardant que chiffres / virgule / point / signe, le reste devient une espace normale.
const fmt2 = (v) => Number(v || 0)
  .toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  .replace(/[^0-9,.\-]+/g, ' ');

const fmtDateFR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const sanitizeFileName = (value) =>
  String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

// ── Mise en page PDF ──────────────────────────────────────────────
const MG = 10;          // marge (mm)
const FICHE_H = 27;     // hauteur d'une fiche ouvrier (mm)
const SUMMARY_H = 16;   // hauteur du bloc total par chantier (mm)
const INFOS_RATIO = 0.70;
const SIG_RATIO = 0.15;

const drawSummaryBox = (doc, x, y, totalW, nbOuvriers, totalSalaires) => {
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.setFillColor(245, 245, 245);
  doc.rect(x, y, totalW, SUMMARY_H, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`Nombre d'ouvriers : ${nbOuvriers}`, x + 4, y + SUMMARY_H / 2 + 1.5);

  doc.setFontSize(10);
  doc.text(`Total salaires : ${fmt2(totalSalaires)} DH`, x + totalW - 4, y + SUMMARY_H / 2 + 1.5, { align: 'right' });
};

const drawFicheBox = (doc, row, x, y, infosW, sigW, empW) => {
  const totalW = infosW + sigW + empW;
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.rect(x, y, totalW, FICHE_H);
  doc.line(x + infosW, y, x + infosW, y + FICHE_H);
  doc.line(x + infosW + sigW, y, x + infosW + sigW, y + FICHE_H);

  const padX = 3;
  const labelColor = [0, 0, 0];
  const valueColor = [22, 54, 122];
  let ty = y + 7;

  const field = (label, value, lx) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...labelColor);
    doc.text(label, lx, ty);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...valueColor);
    doc.text(String(value ?? ''), lx + doc.getTextWidth(label) + 2, ty);
  };

  field('ID', row.ID_Ouvrier, x + padX);
  field('CIN', row.CIN, x + padX + 40);
  field('FONCTION', row.Qualification, x + padX + 85);

  ty += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...labelColor);
  doc.text('NOM', x + padX, ty);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...valueColor);
  doc.text(String(row.Ouvrier ?? ''), x + padX + 10, ty);

  ty += 8;
  field('JOURS', fmt2(parseFrNumber(row.Jours)), x + padX);
  field('HEURES', fmt2(parseFrNumber(row.Heures)), x + padX + 40);
  field('SALAIRE', `${fmt2(parseFrNumber(row.salaires))} DH`, x + padX + 85);
};

const drawPageHeader = (doc, pageW, chantierName, periodeLabel, societe, logo) => {
  if (logo?.dataUri) {
    doc.addImage(logo.dataUri, logo.format, MG, MG - 2, logo.w, logo.h);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text(societe || 'CIENCO', MG, MG + 5);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(new Date().toLocaleDateString('fr-FR'), pageW - MG, MG + 5, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`PAIE : ${periodeLabel}`, pageW / 2, MG + 15, { align: 'center' });

  doc.setFontSize(9);
  doc.text(`Société : ${societe}   |   Chantier : ${chantierName}`, pageW / 2, MG + 21, { align: 'center' });

  const totalW = pageW - 2 * MG;
  const infosW = totalW * INFOS_RATIO;
  const sigW = totalW * SIG_RATIO;
  const empW = totalW - infosW - sigW;
  const tableY = MG + 26;

  doc.setFillColor(235, 235, 235);
  doc.rect(MG, tableY, totalW, 7, 'F');
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.rect(MG, tableY, totalW, 7);
  doc.line(MG + infosW, tableY, MG + infosW, tableY + 7);
  doc.line(MG + infosW + sigW, tableY, MG + infosW + sigW, tableY + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text('Infos', MG + infosW / 2, tableY + 5, { align: 'center' });
  doc.text('Signature', MG + infosW + sigW / 2, tableY + 5, { align: 'center' });
  doc.text('Empreinte', MG + infosW + sigW + empW / 2, tableY + 5, { align: 'center' });

  return { y: tableY + 7, infosW, sigW, empW };
};

const FichePaieChantier = ({ onBack }) => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState('');
  const [error, setError] = useState('');
  const [columnIssues, setColumnIssues] = useState([]);
  const [datePaie, setDatePaie] = useState(() => new Date().toISOString().slice(0, 10));
  const [societe, setSociete] = useState('');
  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    api.get('/societes/logos')
      .then((r) => setSocietes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSocietes([]));
  }, []);

  const guessChantierColumn = (cols) =>
    cols.find((c) => c === 'Dernier_Chantier') || cols.find((c) => /chantier/i.test(c)) || '';

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    fileInputRef.current.value = '';
    if (!file) return;

    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!data.length) {
          setError('Fichier vide ou format non reconnu.');
          return;
        }

        const cols = Object.keys(data[0]);
        const chantierCol = guessChantierColumn(cols);
        const chantierList = chantierCol
          ? [...new Set(data.map((r) => String(r[chantierCol] || '').trim()).filter(Boolean))].sort()
          : [];

        const usedCols = new Set();
        const issues = EXPECTED_COLUMNS
          .filter((c) => !cols.includes(c))
          .map((expected) => {
            const found = findClosestColumn(expected, cols, usedCols);
            if (found) usedCols.add(found.column);
            return { expected, found: found?.column || null };
          });

        setColumns(cols);
        setRows(data);
        setChantiers(chantierList);
        setSelectedChantier('');
        setFileName(file.name);
        setColumnIssues(issues);
      } catch (err) {
        console.error(err);
        setError("Impossible de lire ce fichier Excel.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const chantierColumn = guessChantierColumn(columns);
  const filteredRows = selectedChantier && chantierColumn
    ? rows.filter((r) => String(r[chantierColumn] || '').trim() === selectedChantier)
    : rows;

  const summary = useMemo(() => {
    if (!rows.length) return null;
    return {
      nbOuvriers: filteredRows.length,
      totalJours: filteredRows.reduce((s, r) => s + parseFrNumber(r.Jours), 0),
      totalHeures: filteredRows.reduce((s, r) => s + parseFrNumber(r.Heures), 0),
      totalSalaires: filteredRows.reduce((s, r) => s + parseFrNumber(r.salaires), 0),
    };
  }, [filteredRows, rows.length]);

  const canGenerate = rows.length > 0 && !!chantierColumn && !!datePaie && !!societe;

  // On ne propose que Pistas/Rutas pour l'instant — on récupère leur vraie fiche (avec logo) si
  // trouvée dans /societes/logos, sinon un simple bouton texte reste disponible.
  const featuredSocietes = FEATURED_SOCIETES.map((name) => {
    const found = societes.find((s) => String(s.name || '').toUpperCase().includes(name));
    return found || { id: name, name, logo: null };
  });

  const downloadTemplate = () => {
    const exampleRow = {
      OD: 1,
      ID_Ouvrier: 2849,
      Ouvrier: 'ACHETOUR RIDA',
      CIN: 'EE838502',
      Taux: 120,
      Qualification: 'Ouvrier',
      Dernier_Chantier: 'Carrière Zouirat',
      Jours: 20,
      Heures: 44,
      salaires: 2930,
    };
    const ws = XLSX.utils.json_to_sheet([exampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modele');
    XLSX.writeFile(wb, 'modele_paie_par_chantier.xlsx');
  };

  const exportTable = () => {
    if (!rows.length) return;
    const dataToExport = filteredRows.map((r) => {
      const out = {};
      columns.forEach((c) => { out[c] = r[c]; });
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Paie');
    const suffix = selectedChantier ? sanitizeFileName(selectedChantier) : 'tous_chantiers';
    XLSX.writeFile(wb, `paie_${suffix}.xlsx`);
  };

  const generatePdf = async () => {
    if (!canGenerate) return;

    const targets = selectedChantier ? [selectedChantier] : chantiers;
    if (!targets.length) return;

    const societeObj = societes.find((s) => s.name === societe);
    let logo = null;
    if (societeObj?.logo) {
      const dims = await loadImageDims(societeObj.logo);
      const { w, h } = fitLogo(dims, 32, 14);
      logo = {
        dataUri: societeObj.logo,
        format: societeObj.logo.includes('image/png') ? 'PNG' : 'JPEG',
        w, h,
      };
    }

    const periodeLabel = fmtDateFR(datePaie);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    let firstChantier = true;

    targets.forEach((chantierName) => {
      const chantierRows = rows.filter((r) => String(r[chantierColumn] || '').trim() === chantierName);
      if (!chantierRows.length) return;

      if (!firstChantier) doc.addPage();
      firstChantier = false;

      const startPage = doc.internal.getNumberOfPages();
      let layout = drawPageHeader(doc, pageW, chantierName, periodeLabel, societe, logo);
      let y = layout.y;

      chantierRows.forEach((row) => {
        if (y + FICHE_H > pageH - MG - 8) {
          doc.addPage();
          layout = drawPageHeader(doc, pageW, chantierName, periodeLabel, societe, logo);
          y = layout.y;
        }
        drawFicheBox(doc, row, MG, y, layout.infosW, layout.sigW, layout.empW);
        y += FICHE_H;
      });

      if (y + SUMMARY_H > pageH - MG - 8) {
        doc.addPage();
        layout = drawPageHeader(doc, pageW, chantierName, periodeLabel, societe, logo);
        y = layout.y;
      }
      const nbOuvriersChantier = chantierRows.length;
      const totalSalairesChantier = chantierRows.reduce((s, r) => s + parseFrNumber(r.salaires), 0);
      drawSummaryBox(doc, MG, y, layout.infosW + layout.sigW + layout.empW, nbOuvriersChantier, totalSalairesChantier);

      const endPage = doc.internal.getNumberOfPages();
      const totalPagesChantier = endPage - startPage + 1;
      for (let p = startPage; p <= endPage; p++) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(90, 90, 90);
        doc.text(`Paie : ${periodeLabel}`, MG, pageH - 6);
        doc.text(`${p - startPage + 1}/${totalPagesChantier}`, pageW - MG, pageH - 6, { align: 'right' });
      }
    });

    const safeName = selectedChantier ? sanitizeFileName(selectedChantier) : 'tous_chantiers';
    doc.save(`fiche_paie_${sanitizeFileName(societe)}_${safeName}_${datePaie}.pdf`);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <Button variant="link" className="ps-0 text-decoration-none" onClick={onBack}>
            <FaArrowLeft className="me-1" />Retour aux outils
          </Button>
          <h4 className="mb-0 mt-1">
            <FaFileInvoiceDollar className="me-2" />
            Fiches de paie par chantier
          </h4>
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {columnIssues.length > 0 && (
        <Alert variant="warning" className="py-2">
          <div className="fw-bold mb-2">
            ⚠️ Certaines colonnes ne correspondent pas exactement au modèle attendu — l'aperçu reste
            disponible mais les totaux et le PDF peuvent être incomplets tant que ce n'est pas corrigé.
          </div>
          <Table size="sm" borderless className="mb-0" style={{ maxWidth: 520 }}>
            <thead>
              <tr>
                <th className="small text-muted fw-normal">Colonne attendue</th>
                <th></th>
                <th className="small text-muted fw-normal">Trouvée dans votre fichier</th>
              </tr>
            </thead>
            <tbody>
              {columnIssues.map((issue) => (
                <tr key={issue.expected}>
                  <td><Badge bg="success">{issue.expected}</Badge></td>
                  <td className="text-center text-muted">→</td>
                  <td>
                    {issue.found
                      ? <Badge bg="danger">{issue.found}</Badge>
                      : <Badge bg="secondary">aucune colonne trouvée</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap align-items-end gap-3">
          <div>
            <Form.Label className="small fw-bold mb-1 d-block">Fichier Excel</Form.Label>
            <div className="d-flex gap-2">
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                onChange={handleImport}
                style={{ display: 'none' }}
              />
              <Button variant="success" onClick={() => fileInputRef.current.click()}>
                <FaFileImport className="me-2" />
                Importer
              </Button>
              <Button variant="outline-secondary" onClick={downloadTemplate} title="Télécharger un exemple de fichier au bon format">
                <FaFileDownload className="me-2" />
                Modèle
              </Button>
              {rows.length > 0 && (
                <Button variant="outline-success" onClick={exportTable} title="Télécharger le tableau affiché en Excel">
                  <FaFileExcel className="me-2" />
                  Exporter le tableau
                </Button>
              )}
            </div>
          </div>

          {fileName && (
            <Badge bg="light" text="dark" className="border" style={{ marginBottom: 8 }}>
              {fileName} · {rows.length} ligne{rows.length !== 1 ? 's' : ''}
            </Badge>
          )}

          {chantiers.length > 0 && (
            <div>
              <Form.Label className="small fw-bold mb-1 d-block">Chantier</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                <MiniCard label={`Tous (${chantiers.length})`} active={!selectedChantier} onClick={() => setSelectedChantier('')} />
                {chantiers.map((c) => (
                  <MiniCard key={c} label={c} active={selectedChantier === c} onClick={() => setSelectedChantier(c)} />
                ))}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div>
              <Form.Label className="small fw-bold mb-1 d-block">Société</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {featuredSocietes.map((s) => (
                  <MiniCard key={s.id} label={s.name} logo={s.logo} active={societe === s.name} onClick={() => setSociete(s.name)} />
                ))}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div>
              <Form.Label className="small fw-bold mb-1 d-block">Date de paie</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={datePaie}
                onChange={(e) => setDatePaie(e.target.value)}
                style={{ minWidth: 160 }}
              />
            </div>
          )}

          <Button
            variant="primary"
            className="ms-auto"
            disabled={!canGenerate}
            onClick={generatePdf}
            title={!canGenerate && rows.length > 0 ? 'Renseigner la société et la date de paie' : ''}
          >
            <FaPrint className="me-2" />
            {selectedChantier ? 'Générer ce chantier' : 'Générer tous les chantiers'}
          </Button>
        </Card.Body>
      </Card>

      {summary && (
        <Row className="g-3 mb-3">
          <Col xs={6} md={3}>
            <Card body className="text-center py-2">
              <div className="text-muted small">Ouvriers</div>
              <div className="fw-bold fs-5">{summary.nbOuvriers}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center py-2">
              <div className="text-muted small">Total jours</div>
              <div className="fw-bold fs-5">{fmt2(summary.totalJours)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center py-2">
              <div className="text-muted small">Total heures</div>
              <div className="fw-bold fs-5">{fmt2(summary.totalHeures)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center py-2">
              <div className="text-muted small">Total salaires</div>
              <div className="fw-bold fs-5 text-success">{fmt2(summary.totalSalaires)} DH</div>
            </Card>
          </Col>
        </Row>
      )}

      {rows.length > 0 && (
        <Card>
          <Card.Header className="fw-semibold">
            Aperçu des données importées
            {selectedChantier && <span className="text-muted"> — {selectedChantier}</span>}
          </Card.Header>
          <div style={{ overflowX: 'auto', maxHeight: 500 }}>
            <Table size="sm" bordered hover className="mb-0">
              <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                <tr>
                  {columns.map((c) => (
                    <th key={c} className={NUMERIC_COLUMNS.includes(c) ? 'text-end' : ''}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 200).map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c} className={NUMERIC_COLUMNS.includes(c) ? 'text-end' : ''}>
                        {NUMERIC_COLUMNS.includes(c) ? fmt2(parseFrNumber(r[c])) : String(r[c] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {filteredRows.length > 200 && (
            <Card.Footer className="text-muted small">
              Aperçu limité aux 200 premières lignes ({filteredRows.length} au total).
            </Card.Footer>
          )}
        </Card>
      )}
    </div>
  );
};

export default FichePaieChantier;
